import { useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { RouterLinkAs } from '@/lib/router-link';
import {
  Body,
  Box,
  Button,
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  Input,
  PageHeader,
  RadioCard,
  RadioCardGroup,
  Row,
  Screen,
  ScreenBody,
  Stack,
  StepBadge,
  Textarea,
} from '@leanin/ui';
import { BodyWithAside } from '@/components/page-shell';
import {
  GroupImageUploadField,
  type GroupImageUploadFieldHandle,
} from '@/components/groups/GroupImageUploadField';
import { PlacePicker, type PlaceSelection } from '@/components/geo';
import { useCreateCircle } from '@/lib/mutations/groups';
import { useNetwork } from '@/lib/queries/groups';
import { failureFromError } from '@/lib/api-error';

type CircleNewSearch = {
  // Optional — when set, the form creates the circle already linked to this
  // network. Drives a contextual back link and a redirect back to the
  // network admin Circles tab on success.
  parentNetworkId?: string;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_authed/circles/new')({
  validateSearch: (raw: Record<string, unknown>): CircleNewSearch => {
    const v = raw.parentNetworkId;
    // Only accept a well-formed uuid; ignore anything else silently so a
    // malformed link drops back to the standard free-floating form.
    return typeof v === 'string' && UUID_RE.test(v)
      ? { parentNetworkId: v }
      : {};
  },
  component: CircleCreatePage,
});

// Mirrors the onboarding step (/onboarding/cl/create-circle): two cards,
// "The basics" + "How you'll meet".

type Format = 'in_person' | 'virtual' | 'hybrid';
type YesNo = 'yes' | 'no' | null;

const TIPS: ReadonlyArray<{ t: string; s: string }> = [
  {
    t: 'Make it specific',
    s: 'A clear name and description help the right people find you. “London Women in Finance” or “First-time managers 6–18 months in” will attract better members than something generic.',
  },
  {
    t: 'Recruiting members',
    s: 'Know who you want to invite? Use our ready-made templates. Want new members to find you? List your Circle in our directory.',
  },
  {
    t: 'Company or community?',
    s: 'Some Circles are workplace-based — through a formal program or just colleagues who want to connect. Others bring together people from the same city, industry, or life stage.',
  },
  {
    t: 'What you’ll discuss',
    s: 'Each month your Circle tackles a new topic from our resource library. We provide everything you need to run your meeting — end to end, for free.',
  },
  {
    t: 'Share the load',
    s: 'You don’t have to do this alone. Invite a co-lead to share the responsibility — and the fun.',
  },
];

function CircleCreatePage() {
  const { parentNetworkId } = Route.useSearch();
  // Resolve the parent network (when the page was launched from a network
  // admin surface) so the back link can show the network's name. If the
  // network 404s or the viewer can't read it, we silently fall back to the
  // generic Circles label — the form itself still works and the server is
  // the final authority on the parent-link permission.
  const networkQuery = useNetwork(parentNetworkId ?? '', {
    enabled: parentNetworkId !== undefined,
  });
  const parentNetwork =
    parentNetworkId !== undefined && networkQuery.data !== undefined
      ? networkQuery.data
      : null;

  // Step 01 — The basics
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [place, setPlace] = useState<PlaceSelection | null>(null);
  const [affiliated, setAffiliated] = useState<YesNo>(null);
  const [company, setCompany] = useState('');
  // Step 02 — How you'll meet
  const [openToNew, setOpenToNew] = useState<YesNo>(null);
  const [format, setFormat] = useState<Format>('virtual');
  // Step 03 — Photos. Files are staged in the field handles; the circle
  // doesn't exist until Publish, so the field carries no real group id
  // up front. Publish resolves the id from createCircle, then asks each
  // field to commit using that id.
  const coverFieldRef = useRef<GroupImageUploadFieldHandle | null>(null);
  const [coverPending, setCoverPending] = useState(false);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [committingPhotos, setCommittingPhotos] = useState(false);
  const navigate = useNavigate();
  const createCircle = useCreateCircle();

  const canSubmit =
    name.trim().length > 0 &&
    place !== null &&
    affiliated !== null &&
    (affiliated === 'no' || company.trim().length > 0) &&
    openToNew !== null;

  const onSubmit = async () => {
    setSubmitError(null);
    if (!canSubmit) {
      setSubmitError('Please fill in every required field before publishing.');
      return;
    }

    let detail;
    try {
      detail = await createCircle.mutateAsync({
        name: name.trim(),
        ...(description.trim().length > 0
          ? { description: description.trim() }
          : {}),
        place,
        topicIds: [],
        format,
        access: 'public',
        // "Open to new members? Yes" → request-to-join. "No" → invite-only.
        joinPolicy: openToNew === 'yes' ? 'request' : 'invite_only',
        // Tri-state affiliation. `canSubmit` already pinned `affiliated` to
        // a non-null answer, and a 'yes' answer to a non-empty company.
        affiliatedWithCompany: affiliated === 'yes',
        affiliatedCompany: affiliated === 'yes' ? company.trim() : null,
        ...(parentNetworkId !== undefined ? { parentNetworkId } : {}),
      });
    } catch (err) {
      setSubmitError(failureFromError(err, 'Failed to create Circle.').message);
      return;
    }
    // Photos are optional. If any are staged, commit them under the new
    // circle id (passed as the override so we don't have to wait for a
    // re-render of the field's `groupId` prop). A failed photo upload
    // doesn't block the navigate — the circle exists; the leader can
    // retry photos from circle settings. Just surface the message and
    // route forward.
    if (coverPending) {
      setCommittingPhotos(true);
      try {
        await coverFieldRef.current?.commit(detail.id);
      } catch (err) {
        setSubmitError(
          failureFromError(err, 'Circle created, but a photo upload failed. You can retry from circle settings.').message,
        );
      } finally {
        setCommittingPhotos(false);
      }
    }
    // When the circle was launched from a network admin surface, the
    // user expects to land back on that surface with their new circle
    // already in the list. Otherwise route to the new circle's detail
    // page like a standalone create flow.
    if (parentNetworkId !== undefined) {
      navigate({
        to: '/networks/$id/admin',
        params: { id: parentNetworkId },
        search: { section: 'circles' },
      });
    } else {
      navigate({ to: '/circles/$id', params: { id: detail.id } });
    }
  };
  const isPending = createCircle.isPending || committingPhotos;

  // When launched from a network admin surface, the back link and cancel
  // button retarget to that surface and the back-link label doubles as
  // contextual breadcrumb ("Lean In Saigon ←").
  const cancelHref =
    parentNetworkId !== undefined
      ? `/networks/${parentNetworkId}/admin?section=circles`
      : '/circles';
  const backLabel = parentNetwork !== null ? parentNetwork.name : 'Circles';

  return (
    <Screen>
      <ScreenBody gap="xl">
        <PageHeader
          eyebrow="New Circle"
          title="Start a Circle"
          subtitle="A Lean In Circle is a small group of peers who meet regularly to support each other’s personal and professional growth. Each Circle is typically made up of 8–12 people who meet monthly or twice a month."
          back={{ label: backLabel, href: cancelHref }}
          actions={
            <>
              <Button href={cancelHref} linkAs={RouterLinkAs} variant="ghost">Cancel</Button>
              <Button
                variant="primary"
                iconRight={I.arrow}
                onClick={onSubmit}
                disabled={!canSubmit}
                loading={isPending}
              >
                Publish Circle
              </Button>
            </>
          }
        />
        <BodyWithAside
          asideWidth={380}
          aside={
            <Stack gap="lg">
              <Card pad="default">
                <Stack gap="lg">
                  <Stack gap="xs">
                    <Eyebrow tone="accent">Good to know</Eyebrow>
                    <Heading level={3}>
                      Tips for setting up your Circle
                    </Heading>
                  </Stack>
                  <Stack gap="md">
                    {TIPS.map((tip, i) => (
                      <Row key={i} gap="sm" align="flex-start">
                        <StepBadge state="current" n={i + 1} size="sm" />
                        <Stack gap="xs">
                          <Heading level={5}>{tip.t}</Heading>
                          <Body size="sm" color="mute">
                            {tip.s}
                          </Body>
                        </Stack>
                      </Row>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          }
        >
          {/* MAIN */}
          <Stack gap="lg">
            {/* 01 · The basics */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>Step 01</Eyebrow>
                  <Eyebrow tone="accent">The basics</Eyebrow>
                </Stack>
                <Field required>
                  <Field.Label>Circle name</Field.Label>
                  <Field.Description>
                    Choose a name that helps members immediately understand
                    your Circle’s focus, community, or shared goals.
                  </Field.Description>
                  <Field.Control>
                    <Input
                      placeholder="e.g. First-time Managers · NYC"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={120}
                    />
                  </Field.Control>
                </Field>
                <Field>
                  <Field.Label>Description</Field.Label>
                  <Field.Description>
                    3–5 sentences. Who it’s for, what you’ll do, what to expect.
                  </Field.Description>
                  <Field.Control>
                    <Textarea
                      rows={2}
                      placeholder="A monthly Circle for new managers in their first 18 months. We trade calibration notes, scope battles, and the awkward conversations no one warned you about."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      maxLength={2000}
                    />
                  </Field.Control>
                </Field>
                <Field required>
                  <Field.Label>Location</Field.Label>
                  <Field.Description>
                    Select where your Circle will meet — or, if you plan to
                    meet virtually, your location as Circle Leader.
                  </Field.Description>
                  <Field.Control>
                    <PlacePicker
                      mode="city"
                      value={place}
                      onChange={setPlace}
                      placeholder="New York, NY"
                    />
                  </Field.Control>
                </Field>
                <Field required>
                  <Field.Label>Is your Circle affiliated with a company?</Field.Label>
                  <Field.Description>
                    Select this option if your Circle is part of a formal
                    program rolled out by your organization or because your
                    Circle is made up of people from the same organization.
                  </Field.Description>
                  <Field.Control>
                    <RadioCardGroup
                      value={affiliated ?? ''}
                      onValueChange={(v) => setAffiliated(v as 'yes' | 'no')}
                      columns={2}
                      aria-label="Is your Circle affiliated with a company?"
                    >
                      <RadioCard value="yes" title="Yes" />
                      <RadioCard value="no" title="No" />
                    </RadioCardGroup>
                  </Field.Control>
                </Field>
                {affiliated === 'yes' ? (
                  <Field label="Which company?" required>
                    <Input
                      placeholder="e.g. Acme Inc."
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                  </Field>
                ) : null}
              </Stack>
            </Card>

            {/* 02 · How you'll meet */}
            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow>Step 02</Eyebrow>
                  <Eyebrow tone="accent">How you’ll meet</Eyebrow>
                </Stack>
                <Field required>
                  <Field.Label>Is this Circle open to new members?</Field.Label>
                  <Field.Description>
                    Select this option if your Circle is part of a formal
                    program rolled out by your organization or because your
                    Circle is made up of people from the same organization.
                  </Field.Description>
                  <Field.Control>
                    <RadioCardGroup
                      value={openToNew ?? ''}
                      onValueChange={(v) => setOpenToNew(v as 'yes' | 'no')}
                      columns={2}
                      aria-label="Is this Circle open to new members?"
                    >
                      <RadioCard value="yes" title="Yes" />
                      <RadioCard value="no" title="No" />
                    </RadioCardGroup>
                  </Field.Control>
                </Field>
                <Field label="How will your Circle meet?" required>
                  <RadioCardGroup
                    value={format}
                    onValueChange={(v) => setFormat(v as Format)}
                    columns={3}
                    aria-label="Meeting format"
                  >
                    <RadioCard value="virtual" icon={I.globe} title="Virtually" />
                    <RadioCard value="in_person" icon={I.pin} title="In-person" />
                    <RadioCard value="hybrid" icon={I.users} title="Hybrid" />
                  </RadioCardGroup>
                </Field>
              </Stack>
            </Card>

            {/* 03 · Photos */}
            <Card pad="flush">
              <Stack p="xl" gap="xs">
                <Eyebrow>Step 03</Eyebrow>
                <Eyebrow tone="accent">Photos</Eyebrow>
                <Body size="sm" color="mute">
                  Optional — a cover image helps members recognise your
                  Circle. Uploads on Publish.
                </Body>
              </Stack>
              <Box p="xl" style={{ borderTop: '1px solid var(--border)' }}>
                <GroupImageUploadField
                  ref={coverFieldRef}
                  groupKind="circle"
                  // Placeholder identifier — the field doesn't call presign
                  // until commit(), and Publish passes the freshly-created
                  // id as an override at that point.
                  groupId="pending"
                  groupName={name || 'Your Circle'}
                  field="cover"
                  currentImageUrl={null}
                  eyebrowOverride="Circles Cover Image"
                  onPendingChange={setCoverPending}
                />
              </Box>
            </Card>

            {submitError !== null ? (
              <Card pad="default" tone="burgundy">
                <Body color="primary">
                  {submitError}
                </Body>
              </Card>
            ) : null}

            <Row justify="space-between" align="center">
              <Body size="sm" color="mute">
                You can edit any of this after publishing.
              </Body>
              <Row gap="sm">
                <Button href={cancelHref} linkAs={RouterLinkAs} variant="ghost">Cancel</Button>
                <Button
                  variant="primary"
                  iconRight={I.arrow}
                  onClick={onSubmit}
                  disabled={!canSubmit}
                  loading={isPending}
                >
                  Publish Circle
                </Button>
              </Row>
            </Row>
          </Stack>
        </BodyWithAside>
      </ScreenBody>
    </Screen>
  );
}
